import { useCallback } from "react";

import { useGetVersionQuery } from "fond/api";
import { inAddress } from "fond/layers";
import { isAcceptableSize, MAX_PLANNER_EXTENDED_ADDRESS } from "fond/project/polygon";
import { downloadLayersWithinArea } from "fond/project/polygon/download";
import { beginPolygonSelectUpload, POLYGON_SELECT_UPLOAD_ID } from "fond/project/redux";
import { advanceBaseDataTask, failBaseDataTask, startBaseDataTask, succeedBaseDataTask } from "fond/redux/baseDataTasks";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { BOUNDARY_TOO_LARGE, IMPORT_SAVE_ERROR } from "../copy";
import { BaseDataLayerKey } from "../layers";

import { getImportLayers, isBoundaryImportKey } from "./boundary";
import { formatImportResult, ImportCounts } from "./importTask";

/**
 * Return an action that imports the FOND data of base data layers from inside the boundary, as a task of the row whose flyout started it.
 */
export const useImportWithinBoundary = (owner: BaseDataLayerKey): ((layerKeys: readonly BaseDataLayerKey[]) => Promise<void>) => {
  const dispatch = useAppDispatch();
  const { projectId, versionId } = useAppSelector((state) => state.project);
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const boundary = version?.UploadedArea;

  return useCallback(
    async (layerKeys: readonly BaseDataLayerKey[]) => {
      const keys = layerKeys.filter(isBoundaryImportKey);
      if (!boundary || keys.length === 0) return;

      dispatch(startBaseDataTask({ key: owner, source: "boundaryImport", keys, stage: "downloading" }));
      const fail = (error: string) => dispatch(failBaseDataTask({ key: owner, error }));

      if (!isAcceptableSize(boundary)) {
        fail(BOUNDARY_TOO_LARGE);
        return;
      }

      const imports = keys.flatMap(getImportLayers);
      let result;
      try {
        result = await downloadLayersWithinArea(
          imports.map(({ source }) => source),
          boundary
        );
      } catch {
        fail("There was a problem downloading FOND data for this boundary. Please try again.");
        return;
      }

      if (result.status === "QueryTooLarge") {
        fail("There are too many features inside the boundary to import.");
        return;
      }
      if (result.status === "UnsupportedArea") {
        fail("FOND doesn't have data for this area yet.");
        return;
      }
      const { data } = result;
      if (keys.includes("addresses") && data[inAddress].features.length > MAX_PLANNER_EXTENDED_ADDRESS) {
        const remedy = keys.length > 1 ? "Untick Addresses or draw a smaller boundary." : "Draw a smaller area to import addresses.";
        fail(`The boundary contains more than ${MAX_PLANNER_EXTENDED_ADDRESS.toLocaleString()} addresses. ${remedy}`);
        return;
      }

      const files = Object.fromEntries(imports.map(({ source, id }) => [id, [new File([JSON.stringify(data[source])], `${id}.geojson`)]]));
      const counts: ImportCounts = Object.fromEntries(
        keys.map((key) => [key, getImportLayers(key).map(({ source }) => data[source].features.length)])
      );

      dispatch(advanceBaseDataTask({ key: owner, stage: "uploading", uploadId: POLYGON_SELECT_UPLOAD_ID }));
      try {
        await dispatch(beginPolygonSelectUpload(projectId, versionId, files, boundary));
      } catch {
        fail(IMPORT_SAVE_ERROR);
        return;
      }
      dispatch(succeedBaseDataTask({ key: owner, result: [{ message: formatImportResult(counts) }] }));
    },
    [boundary, dispatch, owner, projectId, versionId]
  );
};
